import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { api, apiUrls } from "../utils/api";
import { safeSetItem } from "../utils/safeStorage";

/** localStorage wrapper that silently no-ops in restricted browsers. */
const safeLocalStorage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
  getItem: (name) => {
    try { return localStorage.getItem(name); } catch { return null; }
  },
  setItem: (name, value) => {
    try { localStorage.setItem(name, value); } catch { /* noop */ }
  },
  removeItem: (name) => {
    try { localStorage.removeItem(name); } catch { /* noop */ }
  },
};

export interface AuthTokens {
  access: { token: string; expires: string };
  refresh: { token: string; expires: string };
}

export interface CurrentUser {
  id: string;
  email: string;
  role: string;
  isEmailVerified: boolean;
  subscription?: string;
}

export interface Wallet {
  credits: number;
}

interface AuthState {
  tokens: AuthTokens | null;
  user: CurrentUser | null;
  wallet: Wallet | null;

  setTokens: (tokens: AuthTokens | null) => void;
  setUser: (user: CurrentUser | null) => void;
  setWallet: (wallet: Wallet | null) => void;
  /** Re-fetch wallet from the backend and update the store. */
  refreshWallet: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      tokens: null,
      user: null,
      wallet: null,

      setTokens: (tokens) => {
        if (tokens) safeSetItem("ghibgen_email_submitted", "1");
        set({ tokens });
      },
      setUser: (user) => set({ user }),
      setWallet: (wallet) => set({ wallet }),
      refreshWallet: async () => {
        try {
          const res = await api.get<{ wallet?: Wallet }>(apiUrls.getUser);
          if (res.data.wallet) set({ wallet: res.data.wallet });
        } catch {
          // non-critical — keep stale value
        }
      },
      logout: () => set({ tokens: null, user: null, wallet: null }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({ tokens: state.tokens }),
    },
  ),
);
